/**
 * This service is based on the scrolly-video package by Daniel Kao.
 * @see https://github.com/dkaoster/scrolly-video/blob/main/src/ScrollyVideo.js
 *
 * We can't use this package directly but would like to use the WebCodecs functionality.
 * Therefore we have to use this service of the package and adapt it to our needs.
 */

import { getBrowser } from './browser';
import { decodeVideo } from './video-decoder';

export interface Frame {
  url: string;
  state: 'initial' | 'loading' | 'ready';
  image: ImageBitmap | HTMLImageElement | null;
}

export type ImageSequenceId = 'wmf_950-s-stage' | 'wmf_950-s-features-highlight';

export const serverFramesCountMap: Record<ImageSequenceId, { mobile: number; tablet: number; desktop: number }> = {
  'wmf_950-s-stage': {
    mobile: 250,
    tablet: 250,
    desktop: 125,
  },
  'wmf_950-s-features-highlight': {
    mobile: 301,
    tablet: 300,
    desktop: 300,
  },
};

export class WebCodecsScrubber {
  src: string;
  canvasEl: HTMLCanvasElement;
  context: CanvasRenderingContext2D | null = null;
  videoEl: HTMLVideoElement | null;
  isReady = false;
  onReady = () => {};
  frames: Frame[] = [];
  serverFramesCount: number | null = null;
  currentFrameIndex: number | null = null;

  /**
   * @param src - The video source
   * @param canvasEl - The canvas element to paint the video frames to (if WebCodecs is used)
   * @param videoEl - The video element to use for scrubbing (if video scrubbing is used)
   * @param onReady - Callback function to call when the video is ready
   * @param serverFramesCount - The number of frames to download from the server (if we use frame-by-frame scrubbing)
   */
  constructor({
    src,
    canvasEl,
    videoEl,
    onReady,
    serverFramesCount,
  }: {
    src: string;
    canvasEl: HTMLCanvasElement;
    videoEl: HTMLVideoElement;
    onReady?: () => void;
    serverFramesCount?: number;
  }) {
    this.src = src;
    this.canvasEl = canvasEl;
    this.serverFramesCount = serverFramesCount ?? null;

    if (onReady) {
      this.onReady = onReady;
    }

    this.videoEl = videoEl;
    if (this.videoEl) {
      this.videoEl.src = src;
      this.videoEl.preload = 'auto';
      this.videoEl.tabIndex = 0;
      // @ts-expect-error - This property is not widely supported
      this.videoEl.autobuffer = true;
      this.videoEl.playsInline = true;
      this.videoEl.muted = true;
      this.videoEl.pause();
      this.videoEl.load();
    }

    // Calls decode video to attempt webcodecs method
    void this.decodeVideoSetup();
  }

  /**
   * Uses webCodecs to decode the video into frames
   */
  async decodeVideoSetup() {
    try {
      if (this.serverFramesCount) {
        this.frames = this.setupImageFrames();
        await this.loadFrame(0); // Load the first frame so we can paint it
      } else {
        await decodeVideo(this.src, frame => this.frames.push(frame));
      }
    } catch (error) {
      console.error('Error encountered while decoding video', error);

      // Remove all decoded frames if a failure happens during decoding
      this.frames = [];

      // Force a video reload when videoDecoder fails
      this.videoEl?.load();
    }

    // If no frames, something went wrong
    if (this.frames.length === 0) {
      console.error('No frames were received from webCodecs');
      this.onReady();
      return;
    }

    // Remove the video and add the canvas
    this.context = this.canvasEl.getContext('2d', { alpha: false });

    // Hide the video and add the canvas to the container
    if (this.videoEl) {
      this.videoEl.style.display = 'none';
    }

    // Paint our first frame
    requestAnimationFrame(() => this.paintCanvasFrame(0));

    this.isReady = true;
    this.onReady();
  }

  setupImageFrames(): Frame[] {
    // Download each frame image and prep it up
    return new Array(this.serverFramesCount)
      .fill(null)
      .map((_, index) => {
        const id = (index + 1).toString().padStart(4, '0');
        const url = this.src.replace('[[id]]', id);
        return {
          url,
          state: 'initial',
          image: null,
        };
      })
      .filter(frame => frame !== null) as Frame[];
  }

  async loadFrame(frameIndex: number): Promise<Frame> {
    const frame = this.frames[frameIndex];

    if (frame.state === 'ready' || frame.state === 'loading') {
      return frame;
    }

    frame.state = 'loading';
    const image = new Image();
    image.src = frame.url;

    return new Promise((resolve, reject) => {
      image.onload = () => {
        frame.state = 'ready';
        frame.image = image;
        resolve(frame);
      };
      image.onerror = err => {
        console.error('Error loading frame', frame.url);
        if (typeof err === 'string') {
          reject(new Error(err));
        } else {
          reject(new Error('Failed to load frame'));
        }
      };
    });
  }

  /**
   * Paints the frame of to the canvas
   */
  paintCanvasFrame(frameIndex: number) {
    if (!this.isReady || this.currentFrameIndex === frameIndex) {
      return;
    }

    // Load X frames before and after the current one
    const frameIndexesToLoad = new Set<number>();
    const framesToLoad = 100;
    for (
      let i = Math.max(0, frameIndex - framesToLoad);
      i <= Math.min(this.frames.length - 1, frameIndex + framesToLoad);
      i++
    ) {
      frameIndexesToLoad.add(i);
    }
    [...frameIndexesToLoad].forEach(i => {
      const needsLoading = this.frames[i].state === 'initial';
      if (needsLoading) {
        void this.loadFrame(i);
      }
    });

    // Get the frame and paint it to the canvas
    const currentFrame = this.frames[frameIndex];
    this.currentFrameIndex = frameIndex;

    if (!this.canvasEl || !this.context || !currentFrame) {
      return;
    }

    if (!currentFrame.image) {
      void this.loadFrame(frameIndex);
    } else {
      this.drawImageToCanvas(currentFrame.image as HTMLImageElement);
    }
  }

  drawImageToCanvas(image: ImageBitmap | HTMLImageElement) {
    if (!this.context || !image) {
      return;
    }

    // Make sure the canvas is scaled properly, similar to setCoverStyle
    this.canvasEl.width = (image as HTMLImageElement).width;
    this.canvasEl.height = (image as HTMLImageElement).height;

    // Draw the frame to the canvas context
    this.context.drawImage(image, 0, 0);
  }

  setProgress(progress: number) {
    const frameIndex = Math.floor(progress * this.frames.length);
    if (this.currentFrameIndex === frameIndex) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.paintCanvasFrame(frameIndex);
    });
  }

  destroy() {
    if (this.videoEl) {
      this.videoEl.pause();
      this.videoEl.src = '';
    }
    this.frames = [];
    this.currentFrameIndex = null;
  }
}

export function hasWebCodecsSupport() {
  const browser = getBrowser();
  return (
    browser !== 'safari' &&
    browser !== 'edge' &&
    typeof VideoDecoder === 'function' &&
    typeof EncodedVideoChunk === 'function' &&
    typeof createImageBitmap === 'function'
  );
}
